import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { CalculatingSpinner } from 'components/UI';
import { FunctionComponent, KeyboardEvent } from 'react';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';

interface NormalItemProps {
    label: string;
    value: string | number;
    info?: string;
    topLine?: boolean;
    bottomLine?: boolean;
    loading?: boolean;
    className?: string;
    onClick?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        row: (props: NormalItemProps) => ({
            width: '100%',
            display: 'flex',
            padding: '19px 0px',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: props.bottomLine ? '1px solid #D4D4D4' : undefined,

            ...(props.onClick && {
                'cursor': 'pointer',
                '&:hover, &:focus-visible': {
                    backgroundColor: '#FAFAFA'
                }
            }),

            [theme.breakpoints.down('sm')]: {
                borderTop: props.topLine ? '1px solid #D4D4D4' : undefined,

                ...(props.topLine && {
                    marginTop: 8
                })
            }
        }),
        text: {
            color: '#000',
            fontSize: 14,
            lineHeight: 1,
            fontWeight: 500
        },
        valueGroup: {
            display: 'flex',
            alignItems: 'center'
        },
        chevron: {
            color: '#7A7A7A',
            fontSize: 12,
            marginLeft: 12
        },
        label: {
            display: 'flex',
            alignItems: 'center',
            gap: 6
        },
        infoIcon: {
            fontSize: 14,
            cursor: 'pointer',
            color: '#5E8677'
        }
    })
);

export const NormalItem: FunctionComponent<NormalItemProps> = props => {
    const classes = useStyles(props);

    const keyDownHandler = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            props.onClick?.();
        }
    };

    return (
        <Box
            className={clsx(classes.row, props.className)}
            {...(props.onClick && {
                role: 'button',
                tabIndex: 0,
                onClick: props.onClick,
                onKeyDown: keyDownHandler
            })}
        >
            <Box className={classes.label}>
                <Typography className={classes.text}>{props.label}</Typography>
                {props.info && (
                    <Tooltip
                        content={props.info}
                        placement="top"
                    >
                        <div>
                            <FontAwesomeIcon
                                icon={faInfoCircle}
                                className={classes.infoIcon}
                            />
                        </div>
                    </Tooltip>
                )}
            </Box>
            {props.loading ? (
                <CalculatingSpinner />
            ) : (
                <Box className={classes.valueGroup}>
                    <Typography className={classes.text}>{props.value}</Typography>
                    {props.onClick && (
                        <FontAwesomeIcon
                            icon={faChevronRight}
                            className={classes.chevron}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

export default NormalItem;
